// jurisdiction — "is this property inside the City of Walla Walla limits?" on the
// owner file (docs/city-utility-agreement-plan.md §3).
//
// The API decides it from Walla Walla County's public GIS at owner-intake initiate
// (and again when the address changes), or staff set it with PATCH
// /api/owner-intake/:groupId/jurisdiction. The City utilities agreement is sent only
// for a decided "inside". This module is the wire type + the words the Owners page
// shows; it imports nothing at runtime so it stays testable outside Vite.

export type Jurisdiction = 'city_of_walla_walla' | 'other_city' | 'unincorporated' | 'unknown';

/** GET /api/owner-intake/:groupId/detail → `jurisdiction` (null = never checked). */
export interface OwnerIntakeJurisdiction {
  jurisdiction: Jurisdiction;
  /** The county parcel matched (raw assessor values). */
  parcel?: { geoId: string; propId: number; situs: string };
  source: 'county_gis' | 'staff';
  /** The county lookup's plain-language reason / which other city. */
  note?: string;
  checkedAt: string;
  overriddenBy?: { email: string; at: string };
  /** The parcel number as the county prints it ("36-07-29-13-0008"). */
  parcelLabel?: string;
  /** Staff's first name when staff set it. */
  setBy?: string;
}

/** The choices staff pick from (Set control), in the order shown. */
export const JURISDICTION_CHOICES: ReadonlyArray<{ value: Jurisdiction; label: string }> = [
  { value: 'city_of_walla_walla', label: 'Inside the City of Walla Walla' },
  { value: 'other_city', label: 'In another city' },
  { value: 'unincorporated', label: 'Unincorporated county' },
  { value: 'unknown', label: 'Not sure yet' },
];

export interface JurisdictionLine {
  /** The statement ("Inside the City of Walla Walla limits"). */
  headline: string;
  /** The supporting facts, " · "-joined (county parcel, the county's note, who set it); null when none. */
  detail: string | null;
  /** True when nobody has decided yet — the Set control is the call to action. */
  needsSetting: boolean;
}

/** What the owner file says about the property's jurisdiction. */
export function jurisdictionLine(j: OwnerIntakeJurisdiction | null | undefined): JurisdictionLine {
  if (!j) {
    return {
      headline: "Not checked against county records yet — set it",
      detail: null,
      needsSetting: true,
    };
  }
  const staff = j.source === 'staff';
  const parcel = j.parcelLabel ? `county parcel ${j.parcelLabel}` : null;
  const note = !staff && j.note ? j.note.trim() || null : null;
  const setBy = staff ? `set by ${(j.setBy ?? '').trim() || 'staff'}` : null;
  const join = (...parts: Array<string | null>): string | null => {
    const kept = parts.filter((p): p is string => !!p);
    return kept.length > 0 ? kept.join(' · ') : null;
  };
  switch (j.jurisdiction) {
    case 'city_of_walla_walla':
      return {
        headline: 'Inside the City of Walla Walla limits',
        detail: join(parcel, setBy),
        needsSetting: false,
      };
    case 'other_city':
      return {
        headline: 'Not inside the City of Walla Walla — another city',
        detail: join(note, setBy),
        needsSetting: false,
      };
    case 'unincorporated':
      return {
        headline: 'Not inside the City of Walla Walla — unincorporated county',
        detail: join(parcel, setBy),
        needsSetting: false,
      };
    default:
      return staff
        ? { headline: 'Not decided yet — set it', detail: setBy, needsSetting: true }
        : {
            headline: "Couldn't tell from county records — set it",
            detail: join(note),
            needsSetting: true,
          };
  }
}
